import React, { useState, useEffect, useRef } from 'react';
import { FiUser, FiLogOut, FiMenu, FiChevronDown, FiCheckSquare, FiBarChart2 } from 'react-icons/fi';

import { IoHome, IoAnalytics } from "react-icons/io5";
import { FiClock } from "react-icons/fi";
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { logout } from '../../utils/logout';
import { getProfileImageUrl } from "../../utils/imageHelpers";

import api from '../../utils/api';

function Navbar({ onToggleSidebar }) {
  const location = useLocation();
  const isActive = (path) => location.pathname.startsWith(path);
  const navigate = useNavigate();
  const { user } = useUser();

  const [showMenu, setShowMenu] = useState(false);
  const [showTimesheet, setShowTimesheet] = useState(user && user.role_id != "2");
  const menuRef = useRef();

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const r = await api.get('/timesheet/user-config');
        if (r.data?.status) {
          setShowTimesheet(!!r.data.requires_approval);
        }
      } catch (e) {
        console.error('Failed to fetch timesheet config');
      }
    };

    if (user) {
      if (user.role_id != "2") {
        setShowTimesheet(true);
      } else {
        fetchConfig();
      }
    }
  }, [user]);

  const handleLogout = () => {
    logout();
  };

  const handleProfileRedirect = () => {
    navigate('/profile');
  };

  const toCamelCase = (str) =>
    str
      ?.toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  return (
    <header className="h-15 bg-white shadow-sm flex items-center justify-between px-4 border-b border-neutral-200 min-w-0 relative">
      <div className="flex items-center justify-between w-full">
        {/* Left side */}
        <div className="flex items-center">
          {user?.role_id != 2 && (
            <button
              onClick={onToggleSidebar}
              className="text-gray-700 hover:text-main mr-2 md:inline-block"
            >
              <FiMenu className="text-2xl" />
            </button>
          )}

          {/* 🔹 Clickable Div to redirect to /tickets */}
          <div
            onClick={() => window.location.replace("/tickets")}
            className="flex items-center cursor-pointer"
          >
            <span className="block sm:hidden text-basea font-heading text-main font-medium">
              eNova STS
            </span>
            <span className="hidden sm:flex gap-2 font-heading text-main font-medium flex">
              <img
                src="/logo.png"
                alt="Logo"
                className="w-6 h-6 object-contain"
              />
              <span>eNova Support Ticketing System</span>
            </span>
          </div>
        </div>

        {/* Right side buttons */}
        <div className="flex gap-3">
          {/* Home - HRMS Dashboard */}
          {user?.role_id != 2 && (
            <button
              onClick={() => navigate("/home")}
              className={`relative group p-2 rounded-full 
            ${isActive("/home") ? "bg-gray-100 text-main" : "text-gray-700 hover:bg-gray-100 hover:text-main"}
          `}
            >
              <IoHome className="text-xl" />
              <div className="absolute top-[120%] left-1/2 -translate-x-1/2 hidden group-hover:block w-auto whitespace-nowrap text-xs text-white bg-gray-900 rounded-lg px-3 py-1 shadow-lg z-10">
                Home
              </div>
            </button>
          )}

          {/* Tickets Dashboard */}
          <button
            onClick={() => navigate("/tickets")}
            className={`relative group p-2 rounded-full 
          ${isActive("/tickets") ? "bg-gray-100 text-main" : "text-gray-700 hover:bg-gray-100 hover:text-main"}
        `}
          >
            <FiCheckSquare className="text-xl" />
            <div className="absolute top-[120%] left-1/2 -translate-x-1/2 hidden group-hover:block w-auto whitespace-nowrap text-xs text-white bg-gray-900 rounded-lg px-3 py-1 shadow-lg z-10">
              Tickets Dashboard
            </div>
          </button>

          {/* Timesheet */}
          {showTimesheet && (
            <button
              onClick={() => navigate("/timesheet")}
              className={`relative group p-2 rounded-full 
            ${isActive("/timesheet") ? "bg-gray-100 text-main" : "text-gray-700 hover:bg-gray-100 hover:text-main"}
          `}
            >
              <FiClock className="text-xl" />
              <div className="absolute top-[120%] left-1/2 -translate-x-1/2 hidden group-hover:block w-auto whitespace-nowrap text-xs text-white bg-gray-900 rounded-lg px-3 py-1 shadow-lg z-10">
                Timesheet
              </div>
            </button>
          )}

          {/* Approval Dashboard */}
          {/* Dashboard */}
          <button
            onClick={() => navigate("/dashboard")}
            className={`mr-4 relative group p-2 rounded-full 
          ${isActive("/dashboard") ? "bg-gray-100 text-main" : "text-gray-700 hover:bg-gray-100 hover:text-main"}
        `}
          >
            <IoAnalytics className="text-xl" />
            <div className="absolute top-[120%] left-1/2 -translate-x-1/2 hidden group-hover:block w-auto whitespace-nowrap text-xs text-white bg-gray-900 rounded-lg px-3 py-1 shadow-lg z-10">
              Analytics Dashboard
            </div>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 sm:gap-2 whitespace-nowrap">





        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-800 hover:bg-primary hover:text-white transition"
          >
            <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full overflow-hidden">
              <img
                src={getProfileImageUrl()}
                alt="Profile"
                className="h-full w-full object-cover rounded-full"
              />
            </span>

            <span className="hidden sm:inline font-medium truncate max-w-[8rem]">
              {toCamelCase(user?.first_name)} {toCamelCase(user?.last_name)}
            </span>
            <FiChevronDown className="text-base" />
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-2 bg-white border rounded shadow z-50 w-40 flex flex-col text-sm overflow-hidden">
              <button
                onClick={() => {
                  setShowMenu(false);
                  handleProfileRedirect();
                }}
                className="flex items-center px-4 py-2 hover:bg-gray-100 text-left"
              >
                <FiUser className="mr-2" /> Profile
              </button>
              <button
                onClick={() => {
                  setShowMenu(false);
                  handleLogout();
                }}
                className="flex items-center px-4 py-2 hover:bg-gray-100 text-left"
              >
                <FiLogOut className="mr-2" /> Logout
              </button>
            </div>
          )}
        </div>

      </div>
    </header>
  );
}

export default Navbar;
